// CsvAnmeldungModal.jsx — Sammelanmeldung per CSV für Vereine (18.10.2026)
//
// Ablauf: Vorlage herunterladen → Datei wählen → Vorschau mit Fehlerliste →
// „Prüfen“ (Server-Probelauf) → „Anmelden“. Gespeichert wird erst beim
// letzten Knopf.
//
// Keine zweite Prüflogik:
//   - Form der Zeilen: lib/csvAnmeldung.js
//   - passt der Sportler in die Kategorie: lib/divisionsFilter.js (dieselbe
//     Regel wie Anmeldefenster und Server, Gleichlauf per Test)
//   - alles Übrige (ausgeschrieben, Pflichtangaben, Graduierung, ausgebucht,
//     Gebühr nach startgebuehr.js): POST /api/anmeldungen mit nur_pruefen,
//     beim Speichern derselbe Weg ohne nur_pruefen.
// Neue Sportler (nicht im Verein gefunden) werden erst beim Anmelden über
// POST /api/wettkaempfer angelegt; für sie prüft der Server dann beim
// Speichern (die Anmeldung ist alles oder nichts).
import React, { useEffect, useMemo, useState } from 'react';
import { CSV_VORLAGE, csvAuswerten, gleicherSportler } from '../../lib/csvAnmeldung';
import { passendeDivisionen } from '../../lib/divisionsFilter';
import { skillAusGurt } from '../../lib/skillAusGurt';
import FotoEinwilligung from '../fotos/FotoEinwilligung';
import '../../styles/TurnierAblauf.css';

const istTeam = (d) => /duo|team/i.test(String(d?.Division_Name || '')) || String(d?.Team || '').toLowerCase() === 'team';

async function antwort(res) {
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) {
    const det = Array.isArray(d.details) ? d.details.join(' · ') : '';
    throw new Error([d.message || d.error || `Fehler ${res.status}`, det].filter(Boolean).join(': '));
  }
  return d;
}

export default function CsvAnmeldungModal({ turnier, vereinId, token, onClose, onFertig }) {
  const kopf = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const [divisionen, setDivisionen] = useState([]);
  const [stichtag, setStichtag] = useState(null);
  const [sportler, setSportler] = useState([]);
  const [ladeFehler, setLadeFehler] = useState('');
  const [datei, setDatei] = useState('');
  const [auswertung, setAuswertung] = useState(null);
  const [uebernehmen, setUebernehmen] = useState(true);
  const [server, setServer] = useState({});   // zeile → { ok, fehler, gebuehren }
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState('');
  const [fehler, setFehler] = useState('');
  // Foto-Einwilligung (V15): nach dem Anmelden für die eben angemeldeten
  // Sportler, wie im Anmeldefenster (FotoEinwilligung speichert sofort).
  const [angemeldet, setAngemeldet] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const d = await antwort(await fetch(`/api/divisionen?turnierId=${turnier.turnier_id}&limit=2000&nurFreigegeben=1`, { headers: kopf }));
        setDivisionen(d.data || []); setStichtag(d.altersStichtag || null);
        const w = await antwort(await fetch(`/api/wettkaempfer/verein/${vereinId}`, { headers: kopf }));
        setSportler(w.data || []);
      } catch (e) { setLadeFehler(e.message); }
    })();
  }, [turnier.turnier_id, vereinId, kopf]);

  const vorlageLaden = () => {
    const url = URL.createObjectURL(new Blob([CSV_VORLAGE], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'sammelanmeldung-vorlage.csv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const dateiGewaehlt = async (f) => {
    setFehler(''); setMeldung(''); setServer({}); setAngemeldet([]);
    if (!f) return;
    setDatei(f.name);
    setAuswertung(csvAuswerten(await f.text()));
  };

  // Zeilen anreichern: vorhandener Sportler? Codes ausgeschrieben, passend?
  const zeilen = useMemo(() => {
    if (!auswertung) return [];
    const katalog = new Map(divisionen.map((d) => [String(d.Division_Code).toLowerCase(), d]));
    return auswertung.eintraege.map((e) => {
      const fehlerListe = [...e.fehler];
      const vorhanden = sportler.find((w) => gleicherSportler(e, w)) || null;
      const person = {
        geburtsdatum: e.geburtsdatum, geschlecht: e.geschlecht,
        gewicht: e.gewicht ?? vorhanden?.gewicht,
        skill_level: vorhanden && !(uebernehmen && e.graduierung) ? vorhanden.skill_level : (skillAusGurt(e.graduierung) || vorhanden?.skill_level || 'Beginner'),
        skill_level_kata: vorhanden && !(uebernehmen && e.graduierung) ? vorhanden.skill_level_kata : null,
        skill_level_grappling: vorhanden && !(uebernehmen && e.graduierung) ? vorhanden.skill_level_grappling : null,
      };
      if (!vorhanden && e.gewicht === null) fehlerListe.push('Gewicht fehlt (Pflicht beim Anlegen)');
      const passend = new Set(e.geburtsdatum && e.geschlecht ? passendeDivisionen(divisionen, person, stichtag).map((d) => d.Division_Code) : []);
      const codes = e.codes.map((c) => {
        const d = katalog.get(c.toLowerCase());
        if (!d) return { code: c, fehler: 'nicht ausgeschrieben' };
        if (istTeam(d)) return { code: d.Division_Code, name: d.Division_Name, fehler: 'Duo/Team bitte im Anmeldefenster (Partner nötig)' };
        if (!passend.has(d.Division_Code)) return { code: d.Division_Code, name: d.Division_Name, fehler: 'passt nicht (Alter, Geschlecht, Gewicht oder Gurt)' };
        return { code: d.Division_Code, name: d.Division_Name };
      });
      codes.filter((c) => c.fehler).forEach((c) => fehlerListe.push(`${c.code}: ${c.fehler}`));
      const s = server[e.zeile];
      if (s && !s.ok) fehlerListe.push(s.fehler);
      return { ...e, vorhanden, codesGeprueft: codes, fehlerListe, gebuehren: s?.gebuehren || null };
    });
  }, [auswertung, divisionen, sportler, stichtag, server, uebernehmen]);

  const gute = zeilen.filter((z) => !z.fehlerListe.length);

  // Server-Probelauf für die schon vorhandenen Sportler (je Zeile, damit die
  // Fehler zur richtigen Zeile kommen).
  const pruefen = async () => {
    setBusy(true); setFehler(''); setMeldung('');
    const neu = {};
    for (const z of zeilen.filter((x) => x.vorhanden && !x.fehler.length && x.codesGeprueft.every((c) => !c.fehler))) {
      try {
        const d = await antwort(await fetch('/api/anmeldungen', {
          method: 'POST', headers: kopf,
          body: JSON.stringify({
            turnier_id: turnier.turnier_id, verein_id: vereinId, nur_pruefen: true,
            wettkampfer_ids: [z.vorhanden.wettkaempfer_id],
            wettkampfer_divisionen: [{ wettkaempfer_id: z.vorhanden.wettkaempfer_id, divisionen: z.codesGeprueft.filter((c) => !c.fehler).map((c) => c.code) }],
          }),
        }));
        neu[z.zeile] = { ok: true, gebuehren: (d.data?.anmeldungen || []).map((a) => a.startgebuehr) };
      } catch (e) { neu[z.zeile] = { ok: false, fehler: `Server: ${e.message}` }; }
    }
    setServer(neu);
    setBusy(false);
    setMeldung('Geprüft. Neue Sportler prüft der Server beim Anmelden.');
  };

  const anmelden = async () => {
    if (!gute.length) return;
    setBusy(true); setFehler(''); setMeldung('');
    try {
      const ids = [];
      const jeId = [];
      for (const z of gute) {
        let id = z.vorhanden?.wettkaempfer_id;
        const daten = {
          vorname: z.vorname, nachname: z.nachname, geburtsdatum: z.geburtsdatum, geschlecht: z.geschlecht,
          gewicht: z.gewicht ?? z.vorhanden?.gewicht, gurtfarbe: z.graduierung || z.vorhanden?.gurtfarbe || 'Weiß',
          vereins_id: Number(vereinId),
        };
        if (!id) {
          const d = await antwort(await fetch('/api/wettkaempfer', { method: 'POST', headers: kopf, body: JSON.stringify(daten) }));
          id = d.data?.wettkaempfer_id || d.data?.id || d.wettkaempfer_id || d.id;
          if (!id) throw new Error(`Sportler ${z.vorname} ${z.nachname} angelegt, aber ohne Nummer zurück`);
        } else if (uebernehmen && ((z.gewicht !== null && Number(z.gewicht) !== Number(z.vorhanden.gewicht))
          || (z.graduierung && z.graduierung !== z.vorhanden.gurtfarbe))) {
          const w = z.vorhanden;
          await antwort(await fetch(`/api/wettkaempfer/${id}`, {
            method: 'PUT', headers: kopf,
            body: JSON.stringify({
              ...w, geburtsdatum: String(w.geburtsdatum).slice(0, 10), vereins_id: Number(vereinId),
              gewicht: daten.gewicht, gurtfarbe: daten.gurtfarbe,
            }),
          }));
        }
        ids.push(id);
        jeId.push({ wettkaempfer_id: id, divisionen: z.codesGeprueft.map((c) => c.code) });
      }
      const d = await antwort(await fetch('/api/anmeldungen', {
        method: 'POST', headers: kopf,
        body: JSON.stringify({ turnier_id: turnier.turnier_id, verein_id: vereinId, wettkampfer_ids: ids, wettkampfer_divisionen: jeId }),
      }));
      setMeldung(`${String(d.message || 'Angemeldet').replace(/\.?\s*$/, '.')} Die Liste ist geleert; für weitere Sportler eine neue Datei wählen.`);
      setAngemeldet(ids);
      setAuswertung(null); setServer({}); setDatei('');
      onFertig && onFertig();
    } catch (e) {
      setFehler(`${String(e.message).replace(/\.+$/, '')}. Nichts wurde angemeldet${zeilen.some((z) => !z.vorhanden) ? ' (neu angelegte Sportler bleiben im Verein gespeichert)' : ''}.`);
    } finally { setBusy(false); }
  };

  return (
    <div className="csv-hintergrund" role="dialog" aria-modal="true" aria-label="Sammelanmeldung per Datei">
      <div className="csv-fenster">
        <div className="tw-kopf">
          <div>
            <h2 className="csv-titel">Sammelanmeldung per Datei</h2>
            <p className="ta-klein">{turnier.name}: eine Zeile je Sportler, mehrere Divisionscodes mit | trennen.</p>
          </div>
          <button type="button" className="ta-btn ta-btn-leise" onClick={onClose}>Schließen</button>
        </div>
        {ladeFehler && <div className="ta-meldung ta-meldung-fehler">{ladeFehler}</div>}
        <div className="ta-knoepfe">
          <button type="button" className="ta-btn" onClick={vorlageLaden}>Vorlage herunterladen</button>
          <label className="ta-btn ta-btn-leise csv-datei">
            {datei || 'CSV-Datei wählen …'}
            <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => dateiGewaehlt(e.target.files?.[0])} />
          </label>
        </div>
        {auswertung?.fehler?.length > 0 && <div className="ta-meldung ta-meldung-fehler">{auswertung.fehler.join(' ')}</div>}
        {zeilen.length > 0 && (
          <>
            <label className="csv-option">
              <input type="checkbox" checked={uebernehmen} onChange={(e) => setUebernehmen(e.target.checked)} />
              Gewicht und Graduierung aus der Datei bei vorhandenen Sportlern übernehmen
            </label>
            <div className="csv-tabelle-rahmen">
              <table className="csv-tabelle">
                <thead><tr><th>Zeile</th><th>Sportler</th><th>Kategorien</th><th>Ergebnis</th></tr></thead>
                <tbody>
                  {zeilen.map((z) => (
                    <tr key={z.zeile} className={z.fehlerListe.length ? 'csv-fehler' : 'csv-ok'}>
                      <td>{z.zeile}</td>
                      <td>{z.vorname} {z.nachname}<div className="ta-klein">{z.geburtsdatum ? z.geburtsdatum.split('-').reverse().join('.') : '—'} · {z.vorhanden ? 'im Verein' : 'wird neu angelegt'}</div></td>
                      <td>{z.codesGeprueft.map((c, i) => (
                        <div key={c.code} className={c.fehler ? 'csv-code-fehler' : ''}>{c.code}{c.name ? ` · ${c.name}` : ''}{z.gebuehren?.[i] != null ? ` (${Number(z.gebuehren[i]).toFixed(2).replace('.', ',')} €)` : ''}</div>
                      ))}</td>
                      <td>{z.fehlerListe.length ? <ul>{z.fehlerListe.map((f) => <li key={f}>{f}</li>)}</ul> : 'in Ordnung'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="ta-klein">{gute.length} von {zeilen.length} Zeilen ohne Fehler. Angemeldet werden nur diese; Zeilen mit Fehlern bitte in der Datei korrigieren und neu laden.</p>
          </>
        )}
        {meldung && <div className="ta-meldung ta-meldung-ok" role="status">{meldung}</div>}
        {angemeldet.length > 0 && <FotoEinwilligung turnierId={turnier.turnier_id} wettkaempferIds={angemeldet} />}
        {fehler && <div className="ta-meldung ta-meldung-fehler" role="alert">{fehler}</div>}
        <div className="ta-knoepfe tw-fuss">
          <button type="button" className="ta-btn" disabled={busy || !zeilen.length} onClick={pruefen}>{busy ? 'Prüft …' : 'Prüfen'}</button>
          <button type="button" className="ta-btn ta-btn-haupt" disabled={busy || !gute.length} onClick={anmelden}>{gute.length} Sportler anmelden</button>
        </div>
      </div>
    </div>
  );
}
